import type { FC, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { IconButton } from './icon-button.js'
import styles from './editable-table.module.css'

export interface EditableColumn<T> {
  field: keyof T & string
  headerName: string
  type?: 'text' | 'number'
  minWidth?: number
  width?: number
  editable?: boolean
}

export interface EditableTableProps<T extends { id: string }> {
  rows: T[]
  columns: EditableColumn<T>[]
  /** Render action buttons for a row in view mode */
  renderActions?: (row: T) => ReactNode
  /** Render action buttons for a row in edit mode */
  renderEditActions?: (row: T, onSave: () => void, onCancel: () => void) => ReactNode
  /** Called when a row is updated (after save) */
  onRowUpdate?: (updatedRow: T) => void
  /** Called when a row is deleted */
  onRowDelete?: (row: T) => void
  /** Toolbar content (e.g., "Add" button) */
  toolbar?: ReactNode
  /** ID of row to auto-enter edit mode */
  autoEditId?: string | null
}

export function EditableTable<T extends { id: string }>({
  rows,
  columns,
  renderActions,
  renderEditActions,
  onRowUpdate,
  toolbar,
  autoEditId,
}: EditableTableProps<T>) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<Partial<T>>({})
  const prevAutoEditId = useRef(autoEditId)

  useEffect(() => {
    if (autoEditId && autoEditId !== prevAutoEditId.current) {
      const row = rows.find(r => r.id === autoEditId)
      if (row) {
        setEditingId(row.id)
        setEditValues({ ...row })
      }
    }
    prevAutoEditId.current = autoEditId
  }, [autoEditId, rows])

  const startEdit = (row: T) => {
    setEditingId(row.id)
    setEditValues({ ...row })
  }

  const handleSave = () => {
    if (editingId && onRowUpdate) {
      onRowUpdate(editValues as T)
    }
    setEditingId(null)
    setEditValues({})
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValues({})
  }

  const handleFieldChange = (field: keyof T & string, value: string, type?: string) => {
    setEditValues(prev => ({
      ...prev,
      [field]: type === 'number' ? (value === '' ? undefined : Number(value)) : value,
    }))
  }

  return (
    <div>
      {toolbar}
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.field} className={styles.headerCell} style={{ minWidth: col.minWidth, width: col.width }}>
                {col.headerName}
              </th>
            ))}
            <th className={styles.actionsHeaderCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const isEditing = editingId === row.id
            return (
              <tr
                key={row.id}
                onDoubleClick={() => !isEditing && startEdit(row)}
                style={{ cursor: isEditing ? undefined : 'pointer' }}
              >
                {columns.map(col => (
                  <td key={col.field} className={styles.cell} style={{ minWidth: col.minWidth, width: col.width }}>
                    {isEditing && col.editable ? (
                      <input
                        type={col.type === 'number' ? 'number' : 'text'}
                        className={styles.input}
                        value={String(editValues[col.field] ?? '')}
                        onChange={ev => handleFieldChange(col.field, ev.target.value, col.type)}
                        autoFocus={columns.indexOf(col) === 0}
                      />
                    ) : (
                      String(row[col.field] ?? '')
                    )}
                  </td>
                ))}
                <td className={styles.actionsCell}>
                  {isEditing ? renderEditActions?.(row, handleSave, handleCancel) : renderActions?.(row)}
                </td>
              </tr>
            )
          })}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className={styles.emptyCell}>
                No rows
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

/** Helper: wraps an icon in an IconButton for use in action renderers */
export const TableActionButton: FC<{
  icon: ReactNode
  label: string
  onClick: () => void
  color?: string
}> = ({ icon, label, onClick }) => (
  <IconButton size="small" onClick={onClick} title={label}>
    {icon}
  </IconButton>
)
